import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setcohortData,
  setDataLayout,
} from "../../data/redux/themeSettingSlice";
import ImageWithBasePath from "../imageWithBasePath";
import {
  setMobileSidebar,
  toggleMiniSidebar,
} from "../../data/redux/sidebarSlice";
import { all_routes } from "../../../feature-module/router/all_routes";
import { HorizontalSidebarData } from "../../data/json/horizontalSidebar";
import logo from "../../../assests/logo/ihfc_logo.png";
import profileimage from "../../../assests/logo/avatar-12.jpg";
import UserIconImage from "../../../assests/logo/user-icon.png";
import { toast } from "react-toastify";
import useUserData from "../../../custom_hooks/useUserData";
import axios from "axios";
import webserviceUrl from "../../../API/webserviceUrl";

type ConfigurationField =
  | "is_post_feedback_form_disabled"
  | "is_top_submission_hidden"
  | "is_relieving_letter_enabled";

type ConfigurationState = Record<ConfigurationField, boolean>;

interface Cohort {
  emp_acdmc_crs_id: string | number;
  course_name: string;
}

const configItems: { field: ConfigurationField; label: string }[] = [
  {
    field: "is_post_feedback_form_disabled",
    label: "Disable Post Feedback Form",
  },
  { field: "is_top_submission_hidden", label: "Hide Top Submission" },
  { field: "is_relieving_letter_enabled", label: "Enable Relieving Letter" },
];

const Header = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const dataLayout = useSelector((state: any) => state.themeSetting.dataLayout);
  const Location = useLocation();
  const { userData } = useUserData();

  const [subOpen, setSubopen] = useState<any>("");
  const [subsidebar, setSubsidebar] = useState("");

  const [cohortOptions, setCohortOptions] = useState<Cohort[]>([]);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState<string>("");
  const [cohortFetched, setCohortFetched] = useState(false); // avoids
  // re-calling API on every click
  const token = localStorage.getItem("token");

  const [configuration, setConfiguration] = useState<ConfigurationState>({
    is_post_feedback_form_disabled: false,
    is_top_submission_hidden: false,
    is_relieving_letter_enabled: false,
  });

  const [configurationLoading, setConfigurationLoading] = useState(false);
  const [updatingConfiguration, setUpdatingConfiguration] =
    useState<ConfigurationField | null>(null);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const school_code = localStorage.getItem("school_code");
  const employee_id = localStorage.getItem("employee_id");

  const getConfiguration = async () => {
    if (!school_code) return;

    try {
      setConfigurationLoading(true);

      const resp = await axios.get(webserviceUrl.get_configuration, {
        params: {
          employee_id,
          school_code,
        },
        headers: authHeaders,
      });

      const configData = resp?.data?.data;

      if (configData) {
        setConfiguration({
          is_post_feedback_form_disabled:
            Number(configData.is_post_feedback_form_disabled) === 1,

          is_top_submission_hidden:
            Number(configData.is_top_submission_hidden) === 1,

          is_relieving_letter_enabled:
            Number(configData.is_relieving_letter_enabled) === 1,
        });
      }
    } catch (error) {
      console.error("Error fetching configuration:", error);
    } finally {
      setConfigurationLoading(false);
    }
  };

  // Update one configuration
  const updateConfiguration = async (
    field: ConfigurationField,
    checked: boolean
  ): Promise<void> => {
    if (updatingConfiguration) return;

    const previousValue = configuration[field];

    // Update UI immediately
    setConfiguration((prev) => ({
      ...prev,
      [field]: checked,
    }));

    try {
      setUpdatingConfiguration(field);

      const resp = await axios.post(
        webserviceUrl.edit_configuration,
        {
          [field]: checked ? 1 : 0,
          employee_id,
          school_code,
        },
        {
          headers: authHeaders,
        }
      );

      if (!resp?.data?.success) {
        throw new Error(resp?.data?.message || "Configuration update failed");
      }
    } catch (error) {
      console.error("Error updating configuration:", error);

      // Revert switch if API fails
      setConfiguration((prev) => ({
        ...prev,
        [field]: previousValue,
      }));
    } finally {
      setUpdatingConfiguration(null);
    }
  };

  // Fetch configuration when the component loads
  useEffect(() => {
    getConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCohorts = async () => {
    if (!school_code || cohortLoading) return; // safety check
    try {
      setCohortLoading(true);
      const resp = await axios.post(
        webserviceUrl.get_batches,
        {
          employee_id,
          school_code,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const batchData = resp?.data?.data || [];
      setCohortOptions(batchData);
      setCohortFetched(true);
    } catch (error) {
      console.error("Error fetching cohorts:", error);
    } finally {
      setCohortLoading(false);
    }
  };

  // Call API only the first time the dropdown is opened/clicked
  const handleCohortDropdownOpen = () => {
    if (!cohortFetched) {
      getCohorts();
    }
  };

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    if (title === subOpen) {
      setSubopen("");
    } else {
      setSubopen(title);
    }
  };

  const toggleSubsidebar = (subitem: any) => {
    if (subitem === subsidebar) {
      setSubsidebar("");
    } else {
      setSubsidebar(subitem);
    }
  };

  const mobileSidebar = useSelector(
    (state: any) => state.sidebarSlice.mobileSidebar
  );

  const toggleMobileSidebar = () => {
    dispatch(setMobileSidebar(!mobileSidebar));
  };

  const handleToggleMiniSidebar = () => {
    if (dataLayout === "mini_layout") {
      dispatch(setDataLayout("default_layout"));
      localStorage.setItem("dataLayout", "default_layout");
    } else {
      dispatch(toggleMiniSidebar());
    }
  };

  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((err) => {});
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch((err) => {});
        }
        setIsFullscreen(false);
      }
    }
  };

  const logout = () => {
    localStorage.clear();
    toast.success("Logout Successfully");
    setTimeout(() => (window.location.href = "/login"), 400);
  };

  const selectCohortById = (id: string, options: Cohort[]) => {
    setSelectedCohort(id);

    const selectedCohortObj = options.find(
      (cohort: any) => String(cohort?.emp_acdmc_crs_id) === id
    );

    if (selectedCohortObj) {
      localStorage.setItem("coruse_cohorts", JSON.stringify(selectedCohortObj));
      dispatch(setcohortData(JSON.stringify(selectedCohortObj)));
    } else {
      localStorage.removeItem("coruse_cohorts");
    }
  };

  // onChange now just delegates
  const handleCohortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    selectCohortById(e.target.value, cohortOptions);
  };

  useEffect(() => {
    if (cohortOptions.length > 0 && !selectedCohort) {
      selectCohortById(
        String(cohortOptions[0].emp_acdmc_crs_id),
        cohortOptions
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortOptions, selectedCohort]);

  useEffect(() => {
    if (school_code && !cohortFetched) {
      getCohorts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_code]);

  // Shared Configuration switches, used by BOTH the desktop profile dropdown
  // and the mobile 3-dot menu. idPrefix keeps label/input ids unique, since
  // both menus are rendered in the DOM at the same time.
  const renderConfiguration = (idPrefix: string) => (
    <>
      <h6 className="mb-3">
        <i className="ti ti-settings me-2"></i>
        Configuration
      </h6>

      {configurationLoading ? (
        <div className="text-center py-2">
          <span className="spinner-border spinner-border-sm"></span>
          <span className="ms-2 fs-12">Loading configuration...</span>
        </div>
      ) : (
        configItems.map(({ field, label }) => (
          <div
            key={field}
            className="d-flex justify-content-between align-items-center mb-3"
          >
            <label
              className="form-check-label fs-12 me-3"
              htmlFor={`${idPrefix}-${field}`}
            >
              {label}
            </label>
            <div className="form-check form-switch mb-0">
              <input
                id={`${idPrefix}-${field}`}
                className="form-check-input"
                type="checkbox"
                checked={configuration[field]}
                disabled={updatingConfiguration !== null}
                onChange={(e) => updateConfiguration(field, e.target.checked)}
              />
            </div>
          </div>
        ))
      )}
    </>
  );

  return (
    <>
      {/* Responsive styles for cohort select + forced header responsive behavior */}
      <style>{`
        .cohort-select-wrapper {
          min-width: 200px;
          max-width: 220px;
          flex-shrink: 0;
        }
        .cohort-select-wrapper .form-select {
          height: 38px;
          border-radius: 8px;
          font-size: 13px;
          border: 1px solid #dcdfe6;
          box-shadow: none;
          transition: border-color 0.2s ease;
        }
        .cohort-select-wrapper .form-select:focus {
          border-color: #7c3aed;
          box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.15);
        }
        @media (max-width: 991.98px) {
          .cohort-select-wrapper {
            min-width: 150px;
            max-width: 160px;
            margin-right: 6px !important;
          }
          .cohort-select-wrapper .form-select {
            font-size: 12px;
            height: 34px;
            padding: 4px 8px;
          }
        }
        @media (max-width: 575.98px) {
          .cohort-select-wrapper {
            min-width: 120px;
            max-width: 130px;
          }
        }

        /* ---- FORCED RESPONSIVE FIX ----
           Ensures the desktop header-user block (search toggle, cohort select,
           profile dropdown) collapses on small screens, and the 3-dot
           mobile-user-menu takes over instead — regardless of any
           conflicting rules elsewhere in the template's CSS/SCSS. */
        @media (max-width: 991.98px) {
          .main-header .header-user {
            display: none !important;
          }
          .main-header .mobile-user-menu {
            display: flex !important;
            align-items: center;
          }
        }
        @media (min-width: 992px) {
          .main-header .mobile-user-menu {
            display: none !important;
          }
          .main-header .header-user {
            display: flex !important;
          }
        }

        /* ---- OVERLAP FIX ----
           The row that holds the horizontal nav menu, the cohort
           select, and the profile block was overlapping because its
           children didn't have a consistent flex/gap/min-width setup,
           so wide content (the nav menu, long names) could sit on top
           of neighboring elements instead of pushing them along or
           wrapping. This forces a single predictable row layout:
           the nav menu flexes and scrolls horizontally instead of
           overflowing on top of the cohort select / profile, and
           every direct child gets breathing room via gap. */
        .main-header .header-user .nav.user-menu.nav-list {
          display: flex !important;
          align-items: center !important;
          flex-wrap: nowrap !important;
          gap: 10px;
          width: 100%;
          min-width: 0;
        }
        .main-header .header-user #horizontal-single.sidebar-horizontal {
          flex: 1 1 auto;
          min-width: 0;
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: thin;
        }
        .main-header .header-user #horizontal-single.sidebar-horizontal .nav-menu {
          display: flex !important;
          flex-wrap: nowrap !important;
          white-space: nowrap;
        }
        .main-header .header-user #horizontal-single.sidebar-horizontal .nav-menu .menu-title {
          display: none; /* "Main" label only makes sense in a vertical list; in a horizontal
                              strip it just eats space and pushes things, so hide it here */
        }
        .main-header .header-user .cohort-select-wrapper,
        .main-header .header-user .d-flex.gap-3.align-items-center {
          flex-shrink: 0;
        }
        /* Dropdown menus (profile card, mobile 3-dot menu) need to float
           above everything else in the header row instead of being
           clipped/overlapped by sibling flex items. */
        .main-header .dropdown-menu {
          z-index: 1050;
        }
        .main-header {
          position: relative;
          z-index: 100;
        }
      `}</style>

      {/* Header */}
      <div className="header">
        <div className="main-header" style={{ marginTop: "-10px" }}>
          <div className="header-left">
            <Link to={routes.adminDashboard} className="logo">
              <img src={logo} alt="Logo" />
            </Link>
            <Link to={routes.adminDashboard} className="dark-logo">
              <img src={logo} alt="Logo" />
            </Link>
          </div>

          <Link
            id="mobile_btn"
            onClick={toggleMobileSidebar}
            className="mobile_btn"
            to="#sidebar"
          >
            <span className="bar-icon">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </Link>

          <div className="header-user">
            <div className="nav user-menu nav-list">
              <div
                className="me-auto d-flex align-items-center"
                id="header-search"
              >
                <Link
                  id="toggle_btn"
                  to="#"
                  onClick={handleToggleMiniSidebar}
                  className="btn btn-menubar me-1"
                >
                  <i className="ti ti-arrow-bar-to-left"></i>
                </Link>
              </div>

              <div
                className="sidebar sidebar-horizontal"
                id="horizontal-single"
              >
                <div className="sidebar-menu">
                  <div className="main-menu">
                    <ul className="nav-menu">
                      <li className="menu-title">
                        <span>Main</span>
                      </li>
                      {HorizontalSidebarData?.map((mainMenu, index) => (
                        <React.Fragment key={`main-${index}`}>
                          {mainMenu?.menu?.map((data, i) => (
                            <li className="submenu" key={`menu-${i}`}>
                              <Link
                                to="#"
                                className={`
														${
                              data?.subMenus
                                ?.map((link: any) => link?.route)
                                .includes(Location.pathname)
                                ? "active"
                                : ""
                            } ${subOpen === data.menuValue ? "subdrop" : ""}`}
                                onClick={() => toggleSidebar(data.menuValue)}
                              >
                                <i className={`ti ti-${data.icon}`}></i>
                                <span>{data.menuValue}</span>
                                <span className="menu-arrow"></span>
                              </Link>

                              {/* First-level Submenus */}
                              <ul
                                style={{
                                  display:
                                    subOpen === data.menuValue
                                      ? "block"
                                      : "none",
                                }}
                              >
                                {data?.subMenus?.map((subMenu: any, j) => (
                                  <li
                                    key={`submenu-${j}`}
                                    className={
                                      subMenu?.customSubmenuTwo ? "submenu" : ""
                                    }
                                  >
                                    <Link
                                      to={subMenu?.route || "#"}
                                      className={`${
                                        subMenu?.subMenusTwo
                                          ?.map((link: any) => link?.route)
                                          .includes(Location.pathname) ||
                                        subMenu?.route === Location.pathname
                                          ? "active"
                                          : ""
                                      } ${
                                        subsidebar === subMenu.menuValue
                                          ? "subdrop"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        toggleSubsidebar(subMenu.menuValue)
                                      }
                                    >
                                      <span>{subMenu?.menuValue}</span>
                                      {subMenu?.customSubmenuTwo && (
                                        <span className="menu-arrow"></span>
                                      )}
                                    </Link>

                                    {/* Check if `customSubmenuTwo` exists */}
                                    {subMenu?.customSubmenuTwo &&
                                      subMenu?.subMenusTwo && (
                                        <ul
                                          style={{
                                            display:
                                              subsidebar === subMenu.menuValue
                                                ? "block"
                                                : "none",
                                          }}
                                        >
                                          {subMenu.subMenusTwo.map(
                                            (subMenuTwo: any, k: number) => (
                                              <li key={`submenu-two-${k}`}>
                                                <Link
                                                  className={
                                                    subMenuTwo.route ===
                                                    Location.pathname
                                                      ? "active"
                                                      : ""
                                                  }
                                                  to={subMenuTwo.route}
                                                >
                                                  {subMenuTwo.menuValue}
                                                </Link>
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      )}
                                  </li>
                                ))}
                              </ul>
                            </li>
                          ))}
                        </React.Fragment>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Cohort Select — responsive, fetches on first click */}
              <div className="cohort-select-wrapper mb-2 mb-lg-0 me-2">
                <select
                  className="form-select"
                  value={selectedCohort}
                  onClick={handleCohortDropdownOpen}
                  onFocus={handleCohortDropdownOpen}
                  onChange={handleCohortChange}
                  disabled={cohortLoading}
                >
                  <option value="">
                    {cohortLoading
                      ? "Loading cohorts..."
                      : "-- Select Cohort --"}
                  </option>
                  {!cohortLoading &&
                    cohortOptions.map((cohort: any) => (
                      <option
                        key={cohort?.emp_acdmc_crs_id}
                        value={cohort?.emp_acdmc_crs_id}
                      >
                        {cohort?.course_name}
                      </option>
                    ))}
                  {!cohortLoading &&
                    cohortFetched &&
                    cohortOptions.length === 0 && (
                      <option disabled>No cohorts available</option>
                    )}
                </select>
              </div>

              <div className="d-flex gap-3 align-items-center">
                <p className="fs-14 my-auto">Hi, {userData?.name}</p>
                <div className="dropdown profile-dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle d-flex align-items-center"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <span
                      className="avatar avatar-sm online borde border-danger bg-white rounded-circle overflow-hidden"
                      style={{ width: "35px", height: "35px" }}
                    >
                      <img
                        src={UserIconImage}
                        alt="Img"
                        className="img-fluid w-100 h-100 rounded-circle object-fit-cover"
                      />
                    </span>
                  </Link>

                  <div className="dropdown-menu shadow-none">
                    <div className="card mb-0">
                      <div className="card-header">
                        <div className="d-flex align-items-center borde">
                          <span className="avatar avatar-lg me-2 avatar-rounded ">
                            <img src={UserIconImage} alt="img" />
                          </span>
                          <div>
                            <h5 className="mb-0">{userData?.name}</h5>
                            <p className="fs-12 fw-medium mb-0">
                              {userData?.email_id}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="card-footer">
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to="/LeadCV"
                        >
                          <i className="ti ti-file-text me-2"></i> Lead CV
                        </Link>
                      </div>

                      {/* Configuration */}
                      <div className="card-footer">
                        {renderConfiguration("desktop")}
                      </div>

                      <div className="card-footer">
                        <Link
                          className="dropdown-item d-inline-flex align-items-center p-0 py-2"
                          to="#"
                          onClick={logout}
                        >
                          <i className="ti ti-login me-2"></i>Logout
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile 3-dot menu (shown below 992px) */}
          <div className="dropdown mobile-user-menu">
            <Link
              to="#"
              className="nav-link dropdown-toggle"
              data-bs-toggle="dropdown"
              data-bs-auto-close="outside"
              aria-expanded="false"
            >
              <i className="fa fa-ellipsis-v"></i>
            </Link>
            <div
              className="dropdown-menu dropdown-menu-end"
              style={{ minWidth: 260, maxWidth: "90vw" }}
            >
              <Link className="dropdown-item" to={routes.viewprofile}>
                My Profile
              </Link>
              <Link className="dropdown-item" to="/LeadCV">
                Lead CV
              </Link>
              <Link
                className="dropdown-item"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#changePasswordModal"
              >
                Change Password
              </Link>

              <div className="dropdown-divider"></div>
              <div className="px-3 py-2">{renderConfiguration("mobile")}</div>
              <div className="dropdown-divider"></div>

              <Link className="dropdown-item" to="#" onClick={logout}>
                Logout
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* /Header */}

      {/* Change Password Modal */}
      <div
        className="modal fade"
        id="changePasswordModal"
        aria-hidden="true"
        aria-labelledby="changePasswordModalLabel"
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="changePasswordModalLabel">
                Change Password
              </h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <form>
                <div className="mb-3">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter current password"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter new password"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Confirm new password"
                  />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-primary">
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;